import type { TokenBalance } from '@solana/kit';
import { useQuery } from '@tanstack/react-query';
import { erc20Abi, formatUnits, type Address, type Hex } from 'viem';
import { usePublicClient } from 'wagmi';

import { isChainIdSupported } from '@/lib/0x-parser';

import {
  computePollingInterval,
  parseSwapWithRetry,
} from '@/components/Updater/TransactionUpdater/utils';

import { CHAIN_IDS } from '@/constants/chain';

import { useSplTokenBalance } from '@/hooks/useSplTokenBalance';

import type { Transaction } from '@/store/transaction/types';
import { getTxHash } from '@/store/transaction/utils';

import type { EthereumAddress, SolanaHash } from '@/utils/models';
import { getSolanaRpc } from '@/utils/solana';

import { getPostSwapSellAmount } from './utils';

export const postSwapSellAmountQueryKey = (chainId: number, txHash: string | undefined) =>
  ['post-swap-sell-amount', chainId, txHash] as const;

export const postSwapBalanceQueryKey = (
  chainId: number,
  tokenAddress: string | undefined,
  address: string | undefined,
) => ['post-swap-balance', chainId, tokenAddress, address] as const;

/** Realized `mint` delta for `owner` across the confirmed transaction, in base units. */
async function getSolanaReceivedAmount(
  signature: SolanaHash,
  mint: string,
  owner: string,
): Promise<bigint> {
  const tx = await getSolanaRpc()
    .getTransaction(signature, {
      commitment: 'confirmed',
      encoding: 'json',
      maxSupportedTransactionVersion: 1,
    })
    .send();
  const sum = (balances: readonly TokenBalance[] | undefined) =>
    (balances ?? [])
      .filter((balance) => balance.mint === mint && balance.owner === owner)
      .reduce((total, balance) => total + BigInt(balance.uiTokenAmount.amount), 0n);
  return sum(tx?.meta?.postTokenBalances) - sum(tx?.meta?.preTokenBalances);
}

const min = (a: bigint, b: bigint) => (a < b ? a : b);

/**
 * Resolves the buy-token amount a post-swap trigger order can sell, as a decimal
 * string. Prefers the exact amount already on the transaction, then parses it from
 * the mined transaction, and finally falls back to the quoted amount capped at the
 * wallet's current balance so the order can never exceed what the wallet holds.
 * Empty while unresolved.
 */
export function usePostSwapSellAmount({
  tx,
  address,
  enabled,
}: {
  tx: Transaction | undefined;
  address: string | undefined;
  enabled: boolean;
}): string {
  const storedAmount = tx ? getPostSwapSellAmount(tx) : '';
  const chainId = tx?.buyToken.chainId ?? 0;
  const tokenAddress = tx?.buyToken.address;
  const isSolana = chainId === CHAIN_IDS.SOLANA;
  const txHash = tx ? getTxHash(tx) : undefined;
  const owner = tx?.recipient ?? address;
  const publicClient = usePublicClient({ chainId: isSolana ? undefined : chainId });

  // Cross-chain settles through a bridge fill, not a Settler call, so parseSwap
  // cannot attribute its output; it goes straight to the capped quote.
  const canParse =
    enabled &&
    !!tx &&
    !storedAmount &&
    !!txHash &&
    tx.transactionType !== 'cross-chain' &&
    (isSolana ? !!owner : isChainIdSupported(chainId) && !!publicClient);

  const parsed = useQuery({
    queryKey: postSwapSellAmountQueryKey(chainId, txHash),
    queryFn: async (): Promise<string> => {
      if (isSolana) {
        const received = await getSolanaReceivedAmount(txHash as SolanaHash, tokenAddress!, owner!);
        return received > 0n ? formatUnits(received, tx!.buyToken.decimals) : '';
      }
      const swap = await parseSwapWithRetry({
        publicClient: publicClient!,
        transactionHash: txHash as Hex,
        smartContractWallet:
          tx!.executionMode === 'eip7702' ? (address as EthereumAddress) : undefined,
      });
      return swap?.tokenOut.amount ?? '';
    },
    enabled: canParse,
    staleTime: Infinity,
    retry: false,
  });

  const needsFallback =
    enabled && !!tx && !storedAmount && (!canParse || parsed.isError || parsed.data === '');

  // Read directly rather than through the shared wagmi balance query: that query
  // may still hold the pre-swap balance, and its refresh interval is seconds
  // long. Keep polling while a lagging node still reports the pre-swap zero.
  const { data: evmBalance } = useQuery({
    queryKey: postSwapBalanceQueryKey(chainId, tokenAddress, address),
    queryFn: () =>
      publicClient!.readContract({
        address: tokenAddress as Address,
        abi: erc20Abi,
        functionName: 'balanceOf',
        args: [address as Address],
      }),
    enabled: needsFallback && !isSolana && !!publicClient && !!tokenAddress && !!address,
    refetchInterval: (query) => (query.state.data === 0n ? computePollingInterval(chainId) : false),
  });
  const { data: splBalance } = useSplTokenBalance({
    tokenAddress: needsFallback && isSolana ? tokenAddress : undefined,
    accountAddress: address,
  });

  if (!tx) return '';
  if (storedAmount) return storedAmount;
  if (parsed.data) return parsed.data;
  if (!needsFallback) return '';

  const balance = isSolana ? splBalance?.amount : evmBalance;
  if (balance == null) return '';
  const capped = min(BigInt(balance), BigInt(tx.buyAmount));
  return capped > 0n ? formatUnits(capped, tx.buyToken.decimals) : '';
}
